'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DirectoryRow {
  slug: string
  name: string
  credential: string
  industry: string
  /** Short bundle line, e.g. '3 PDFs, live quiz, notes, 12-week plan & more'. */
  summary: string
  poster: string | null
  posterAlt: string
  /** Lowercased haystack the search query is matched against. */
  keywords: string
  accentTint: string
  accentBorder: string
}

interface ExamDirectoryProps {
  rows: DirectoryRow[]
}

function countLabel(n: number, filtering: boolean): string {
  if (filtering) return `${n} ${n === 1 ? 'match' : 'matches'}`
  return `${n} ${n === 1 ? 'exam' : 'exams'}`
}

/** Searchable, filterable exam directory (design-mockup spec). */
export function ExamDirectory({ rows }: ExamDirectoryProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [q, setQ] = useState('')
  const [industry, setIndustry] = useState('all')

  // Global "/" focuses search; Escape clears and blurs — keyboard subscription.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const input = inputRef.current
      if (!input) return
      if (e.key === '/' && document.activeElement !== input) {
        e.preventDefault()
        input.focus()
      } else if (e.key === 'Escape' && document.activeElement === input) {
        setQ('')
        input.value = ''
        input.blur()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const industries = useMemo(
    () => [...new Set(rows.map((r) => r.industry))],
    [rows]
  )

  const query = q.trim().toLowerCase()
  const filtering = query.length > 0
  const sections = industries
    .map((name) => ({
      industry: name,
      rows: rows.filter(
        (r) =>
          r.industry === name &&
          (industry === 'all' || industry === name) &&
          (!filtering || r.keywords.includes(query))
      ),
    }))
    .filter((s) => s.rows.length > 0)

  function clearAll(): void {
    setQ('')
    setIndustry('all')
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <>
      <section className="mx-auto max-w-245 px-5 pb-11 md:px-6">
        <div className="flex flex-col gap-3.5">
          <div className="relative">
            <Search
              size={18}
              aria-hidden
              strokeWidth={2.2}
              className="text-hint pointer-events-none absolute top-1/2 left-5.5 -translate-y-1/2"
            />
            <input
              ref={inputRef}
              type="text"
              aria-label="Search exams"
              placeholder="Search exams by name, org, or industry…"
              onChange={(e) => setQ(e.target.value)}
              className="bg-bg text-ink placeholder:text-hint border-border focus:border-primary h-14.5 w-full rounded-full border pr-14 pl-13 text-base shadow-[0_8px_24px_rgba(17,24,39,0.04)] transition-[border-color,box-shadow] duration-150 outline-none focus:shadow-[0_0_0_3px_rgba(29,78,216,0.10)]"
            />
            {q ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setQ('')
                  if (inputRef.current) {
                    inputRef.current.value = ''
                    inputRef.current.focus()
                  }
                }}
                className="bg-mist hover:bg-border absolute top-1/2 right-3 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-colors"
              >
                <X
                  size={14}
                  aria-hidden
                  strokeWidth={2.4}
                  className="text-body"
                />
              </button>
            ) : (
              <span
                aria-hidden
                className="text-hint border-border pointer-events-none absolute top-1/2 right-5.5 -translate-y-1/2 rounded-[7px] border px-2.25 py-0.75 text-xs font-bold"
              >
                /
              </span>
            )}
          </div>

          <div className="flex gap-2.5 overflow-x-auto p-0.5 sm:justify-center">
            {['all', ...industries].map((value) => {
              const active = industry === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setIndustry(value)}
                  className={cn(
                    'h-11 shrink-0 cursor-pointer rounded-full border px-5.5 text-[14.5px] font-bold whitespace-nowrap transition-colors',
                    active
                      ? 'bg-chip-active border-chip-active-border text-primary'
                      : 'bg-bg border-border text-body'
                  )}
                >
                  {value === 'all' ? 'All' : value}
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {sections.map((section) => (
        <section
          key={section.industry}
          className="mx-auto max-w-245 px-5 pb-12 md:px-6"
        >
          <div className="mb-4.5 flex items-baseline gap-3.5">
            <h2 className="text-ink text-[22px] font-bold tracking-[-0.02em]">
              {section.industry}
            </h2>
            <span className="text-hint text-sm">
              {countLabel(section.rows.length, filtering)}
            </span>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {section.rows.map((row) => (
              <Link
                key={row.slug}
                href={`/free-study-materials/${row.slug}`}
                aria-label={`See the free ${row.name} study materials`}
                style={{
                  '--accent-tint': row.accentTint,
                  '--accent-border': row.accentBorder,
                }}
                className="focus-visible:outline-primary block rounded-2xl no-underline focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                <article className="ps-card flex h-full flex-col rounded-2xl border border-(--accent-border) bg-(--accent-tint) p-5 pb-3.5 md:p-6 md:pb-4">
                  <div className="flex items-center gap-4 pb-4 md:gap-5">
                    <div className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-baseline gap-x-2.5">
                        <h3 className="font-heading text-ink text-lg font-bold md:text-xl">
                          {row.name}
                        </h3>
                        <span className="text-hint text-[13px]">
                          {row.credential}
                        </span>
                      </span>
                      <p className="text-body mt-2 text-sm leading-relaxed">
                        {row.summary}.
                      </p>
                    </div>
                    {row.poster ? (
                      <span className="block shrink-0 overflow-hidden rounded-[14px]">
                        <Image
                          src={row.poster}
                          alt={row.posterAlt}
                          width={128}
                          height={128}
                          loading="lazy"
                          sizes="128px"
                          className="ps-zoomable h-28 w-28 object-cover md:h-32 md:w-32"
                        />
                      </span>
                    ) : (
                      <span className="bg-bg text-ink flex h-28 w-28 shrink-0 items-center justify-center rounded-[14px] text-[19px] font-bold tracking-[-0.02em] md:h-32 md:w-32">
                        {row.name}
                      </span>
                    )}
                  </div>
                  <span className="border-ink/10 text-hint mt-auto block border-t pt-2.5 text-center text-[10px] font-medium tracking-wide">
                    Click to view details
                  </span>
                </article>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {sections.length === 0 && (
        <section className="mx-auto max-w-245 px-5 pb-12 md:px-6">
          <div className="bg-bg border-border flex flex-col items-center gap-2.5 rounded-[18px] border px-8 py-12 text-center">
            <p className="text-ink text-lg font-bold">
              No exams match &ldquo;{q.trim()}&rdquo;
            </p>
            <p className="text-body text-[14.5px]">
              Try a shorter name or a different spelling — or browse everything.
            </p>
            <button
              type="button"
              onClick={clearAll}
              className="bg-primary hover:bg-primary-hover mt-2 h-11.5 cursor-pointer rounded-full px-6.5 text-[15px] font-bold text-white transition-colors"
            >
              Show all exams
            </button>
          </div>
        </section>
      )}
    </>
  )
}
